import { useCallback, useEffect, useRef } from 'react'
import IMask, { InputMask } from 'imask'

interface UsePhoneMaskOptions {
  onAccept?: (value: string) => void
}

export function usePhoneMask(options: UsePhoneMaskOptions = {}) {
  const maskRef = useRef<InputMask | null>(null)
  const onAcceptRef = useRef<UsePhoneMaskOptions['onAccept']>(options.onAccept)

  // Keep latest onAccept without re-binding listeners
  useEffect(() => {
    onAcceptRef.current = options.onAccept
  }, [options.onAccept])

  const createMask = useCallback((element: HTMLInputElement) => {
    // Destroy existing mask if any
    if (maskRef.current) {
      maskRef.current.destroy()
    }

    // Create new mask for US phone numbers with country code
    maskRef.current = IMask(element, {
      mask: '+1 (000) 000-0000',
      lazy: false, // Show placeholder immediately
      placeholderChar: '_',
    })

    // Handle events
    maskRef.current.on('accept', () => {
      const unmaskedValue = maskRef.current?.unmaskedValue || ''
      onAcceptRef.current?.(unmaskedValue)
    })

    return maskRef.current
  }, [])

  const setValue = useCallback((value: string) => {
    if (!maskRef.current) return
    const currentUnmasked = maskRef.current.unmaskedValue || ''
    if (currentUnmasked === value) return
    // Set unmasked value so IMask formats it according to the mask
    maskRef.current.unmaskedValue = value
  }, [])

  const destroy = useCallback(() => {
    if (maskRef.current) {
      maskRef.current.destroy()
      maskRef.current = null
    }
  }, [])

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      destroy()
    }
  }, [destroy])

  return {
    createMask,
    setValue,
    destroy,
  }
}
